import { useQuery } from '@tanstack/react-query';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import { getRelatedResourcesDiscoveriesCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryNotify } from '../useQueryCallbacks';
import { getIntl } from '../../utils/intl';
import { v4 as uuid } from 'uuid';

const intl = getIntl('useRelatedResourceDiscoveries', {
    errorGettingRelatedResourceDiscoveries: 'Error getting related resource discoveries',
});

export const discoveryToResource = (
    discovery: ArrayElement<APITypes.medicalDataService.GetRelatedResourceDiscoveriesResponse['discoveries']>,
): MedicalDataTypes.EditablePrimaryRecordResource => {
    // ids are ultimately ignored on creation, but useful for the UI to track discoveries
    const { name, type, explanation } = discovery;
    if (type === 'condition') {
        const condition: MedicalDataTypes.PrimaryRecordCondition = {
            version: '1',
            type: 'condition',
            name,
            status: 'active',
            notes: explanation,
            id: uuid(),
        };
        return condition;
    } else if (type === 'medication') {
        const medication: MedicalDataTypes.PrimaryRecordMedication = {
            name,
            type,
            notes: explanation,
            version: '1',
            fdaName: null,
            genericNameOrActiveIngredient: null,
            strength: null,
            form: null,
            route: null,
            quantity: null,
            frequency: null,
            isActive: true,
            whenToTake: null,
            numberOfRefills: null,
            lastRefillDateTime: null,
            providerCareTeamMemberId: null,
            providerName: null,
            id: uuid(),
            asNeeded: null,
        };
        return medication;
    } else if (type === 'allergy') {
        const allergy: MedicalDataTypes.PrimaryRecordAllergy = {
            version: '1',
            type: 'allergy',
            name,
            status: 'active',
            notes: explanation,
            allergyType: null,
            id: uuid(),
        };
        return allergy;
    } else if (type === 'visit') {
        const visit: MedicalDataTypes.PrimaryRecordVisit = {
            version: '1',
            type: 'visit',
            visitType: null,
            visitTypeOtherLabel: null,
            reason: null,
            duration: null,
            notes: explanation,
            providerCareTeamMemberId: null,
            providerName: null,
            address1: null,
            address2: null,
            city: null,
            state: null,
            zipCode: null,
            id: uuid(),
        };
        return visit;
    } else {
        throw new Error(`Unknown discovery type: ${type}`);
    }
};

export type UseRelatedResourceDiscoveriesArgs = {
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    resourceId: string;
    relatedCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
};

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

export type DiscoveryItem = {
    asResource: MedicalDataTypes.EditablePrimaryRecordResource;
} & ArrayElement<APITypes.medicalDataService.GetRelatedResourceDiscoveriesResponse['discoveries']>;

export type UseRelatedResourceDiscoveriesResult = {
    isLoading: boolean;
    discoveries: DiscoveryItem[] | null;
};

export default function useRelatedResourceDiscoveries({
    profileId,
    category,
    resourceId,
    relatedCategory,
}: UseRelatedResourceDiscoveriesArgs): UseRelatedResourceDiscoveriesResult {
    const query = useQuery({
        queryKey: getRelatedResourcesDiscoveriesCacheKey(profileId, category, resourceId),
        queryFn: async () => {
            const result = await appAPI.get<APITypes.medicalDataService.GetRelatedResourceDiscoveriesResponse>(
                urls.api.medicalData.relatedResourceDiscoveries(profileId, category, resourceId, relatedCategory),
            );

            return result.discoveries.map<DiscoveryItem>((discovery) => ({
                ...discovery,
                asResource: discoveryToResource(discovery),
            }));
        },
    });

    useQueryNotify({
        query,
        errorMessage: intl('errorGettingRelatedResourceDiscoveries'),
    });

    return {
        isLoading: query.isFetching,
        discoveries: query.data || null,
    };
}
